import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { BrandImg } from "@/components/site/Brand";
import { scrollToId } from "@/hooks/useLenis";

const EASE = [0.22, 1, 0.36, 1];

const HERO_LINES = [
  "Develop leaders.",
  "Strengthen teams.",
  <>
    Bring <em className="italic text-rc-copper">vision</em> to life.
  </>,
];

const HERO_PHOTOS = [
  { src: "/images/CMP-79.webp", pos: "50% 50%" },
  { src: "/images/CMP-61.webp", pos: "52% 50%" },
  { src: "/images/CMP-20.webp", pos: "43% 50%" },
];

const ArchFrame = ({ imgY, rx, ry }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_PHOTOS.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
  <div className="relative mx-auto w-full max-w-[460px] [perspective:1200px]">
    <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] border border-rc-line" data-testid="hero-photo-frame">
      <motion.div style={{ y: imgY, scale: 1.08 }} className="absolute inset-0">
        {HERO_PHOTOS.map((p, i) => (
          <motion.img
            key={p.src}
            src={p.src}
            alt="Reggie and Chaquanna Rhett, co-founders of The Rhett Co"
            initial={false}
            animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1 : 1.06 }}
            transition={{ duration: 1.6, ease: EASE }}
            style={{ objectPosition: p.pos }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ))}
      </motion.div>
      <div className="absolute bottom-5 right-6 flex gap-1.5">
        {HERO_PHOTOS.map((p, i) => (
          <button key={p.src} onClick={() => setIdx(i)} aria-label={`Show photo ${i + 1}`} data-testid={`hero-photo-dot-${i}`} className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${i === idx ? "w-6 bg-rc-copper" : "w-1.5 bg-rc-ink/60"}`} />
        ))}
      </div>
    </div>
    <motion.div
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      className="absolute -bottom-12 left-3 w-28 rounded-2xl border border-rc-line bg-rc-surface/70 p-3 backdrop-blur-xl sm:-bottom-10 sm:-left-14 sm:w-48 sm:p-5"
      data-testid="hero-monogram-card"
    >
      <BrandImg kind="rco" alt="RCo monogram" className="w-full [transform:translateZ(40px)]" />
      <p className="mt-3 font-mono-rc text-[10px] uppercase tracking-[0.25em] text-rc-muted [transform:translateZ(20px)]">Charlotte · NC</p>
    </motion.div>
  </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.3);
  const rx = useSpring(useTransform(my, [0, 1], [14, -14]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [0, 1], [-18, 18]), { stiffness: 110, damping: 16 });
  const glow = useMotionTemplate`radial-gradient(600px circle at ${useTransform(mx, (v) => `${v * 100}%`)} ${useTransform(my, (v) => `${v * 100}%`)}, rgba(196,131,43,0.14), transparent 60%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="relative min-h-screen overflow-hidden pt-[72px]" data-testid="hero-section">
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: glow }} />
      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-5 pb-24 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pt-20">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="font-mono-rc text-xs uppercase tracking-[0.28em] text-rc-copper"
          >
            The Rhett Co LLC — Charlotte, North Carolina
          </motion.p>
          <MaskLines
            as="h1"
            onLoad
            delay={0.35}
            lines={HERO_LINES}
            testId="hero-heading"
            className="mt-8 font-display text-[10.5vw] font-medium leading-[0.98] tracking-[-0.02em] text-rc-ink lg:text-[6.4vw] 2xl:text-[92px]"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="mt-10 max-w-xl"
          >
            <p className="text-base leading-relaxed text-rc-body md:text-lg">
              Music production, worship leadership, and strategic consulting for churches, artists, and creative organizations.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollToId("inquire")}
                data-testid="hero-inquire-btn"
                className="group flex items-center gap-3 rounded-full bg-rc-copper py-3.5 pl-7 pr-3.5 font-medium text-rc-bg transition-[background-color,transform] duration-300 hover:bg-rc-glow active:scale-95"
              >
                Start an inquiry
                <span className="grid h-8 w-8 place-items-center rounded-full bg-rc-bg text-rc-copper transition-transform duration-500 group-hover:rotate-45">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </button>
              <button
                onClick={() => scrollToId("services")}
                data-testid="hero-services-btn"
                className="group flex items-center gap-2 rounded-full border border-rc-line px-7 py-3.5 text-rc-ink transition-[border-color,background-color] duration-300 hover:border-rc-copper hover:bg-rc-surface"
              >
                Explore services
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
          className="relative lg:col-span-4 lg:-ml-10 lg:pt-6"
        >
          <ArchFrame imgY={imgY} rx={rx} ry={ry} />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative mx-auto flex max-w-[1440px] items-center justify-between border-t border-rc-line px-5 py-6 font-mono-rc text-[11px] uppercase tracking-[0.3em] text-rc-muted sm:px-8 lg:px-12"
      >
        <span>Artist <span className="text-rc-copper">•</span> Producer <span className="text-rc-copper">•</span> Worship Consultant</span>
        <span className="hidden sm:block">Scroll</span>
      </motion.div>
    </section>
  );
};
